import {
  CheckboxGroup,
  Divider,
  Typography,
  IMAGE_HEIGHT,
  imageResize,
  DialogPosition,
  Dialog,
  ImageInput,
  Alert,
  IMAGE_WIDTH,
} from '@ecdlink/ui';
import { useSessionStorage } from '@ecdlink/core';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { useEffect, useState, useMemo } from 'react';
import { DynamicFormProps } from '../../dynamic-form';
import SignatureCanvas from 'react-signature-canvas';
import { useAppDispatch } from '@/store';
import { DateFormats } from '../../../../../../constants/Dates';
import { useSelector } from 'react-redux';
import { coachSelectors } from '@/store/coach';
import { documentThunkActions } from '@/store/document';
import { FileTypeEnum } from '@ecdlink/graphql';
import { PhotoPrompt } from '@/components/photo-prompt/photo-prompt';
import { imageExtensions } from '@/constants/AcceptableImageExtensions';
import { getSectionsQuestionsByStep } from '@/store/pqa/pqa.selectors';
import { practitionerVisitIdKey } from '@/pages/practitioner/practitioner-profile/practitioner-journey/forms';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

export const step18VisitSection = 'Step 18';

export const FinalStepReaccreditation = ({
  isView,
  setSectionQuestions,
  setEnableButton,
  smartStarter,
}: DynamicFormProps) => {
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();

  const name =
    smartStarter?.user?.firstName ||
    smartStarter?.firstName ||
    'the SmartStarter';

  const coach = useSelector(coachSelectors.getCoach);
  const formattedDate = `${new Date().toLocaleString(
    'en-za',
    DateFormats.dayFullMonthYear
  )}`;

  const location = useMemo(() => {
    const address = [
      smartStarter?.siteAddress?.addressLine1,
      smartStarter?.siteAddress?.addressLine2,
      smartStarter?.siteAddress?.addressLine3,
      smartStarter?.siteAddress?.province?.description,
    ];

    if (address.every((item) => item === null || item === undefined)) {
      return 'No address available';
    }

    return `${address[0] || ''} ${address[1] || ''} ${address[2] || ''} ${
      address[3]
    }`;
  }, [smartStarter]);

  const [siteImageOne, setSiteImageOne] = useState<string>('');
  const [siteImageTwo, setSiteImageTwo] = useState<string>('');

  const [isPhotoActionBarVisibleOne, setPhotoActionBarVisibleOne] =
    useState(false);
  const [isPhotoActionBarVisibleTwo, setPhotoActionBarVisibleTwo] =
    useState(false);

  useEffect(() => {
    if (
      (siteImageOne === '' || siteImageOne === null) &&
      (siteImageTwo !== '' || siteImageTwo !== null)
    ) {
      setSiteImageOne(siteImageTwo);
      setSiteImageTwo('');
      updateAnswer(1, '');
    }
  }, [siteImageOne, siteImageTwo]);

  const [visitIdFromPractitionerJourney] = useSessionStorage(
    practitionerVisitIdKey
  );

  const previousData = useSelector(
    getSectionsQuestionsByStep(
      visitIdFromPractitionerJourney ?? '',
      'reAccreditationPreviousFormData',
      step18VisitSection
    )
  );

  const sortedQuestions = useMemo(() => {
    if (previousData?.questions) {
      return [...previousData.questions].sort((a, b) =>
        a.question.localeCompare(b.question)
      );
    }
    return [];
  }, [previousData]);

  const [questions, setAnswers] = useState([
    {
      question: 'First photo',
      answer: '',
    },
    {
      question: 'Second photo',
      answer: '',
    },
    {
      question: `I, ${coach?.user?.firstName}, confirm that I have observed the programme of ${name} at ${location} on this day of ${formattedDate}.`,
      answer: 'False',
    },
    {
      question: 'Practitioner Signature',
      answer: '',
    },
  ]);

  const uploadImage = async (index: number) => {
    if (isOnline) {
      const result = await appDispatch(
        documentThunkActions.createFile({
          base64File: index === 0 ? siteImageOne : siteImageTwo,
          fileName: `SiteImage_${index === 0 ? '1' : '2'}.png`,
          fileType: FileTypeEnum.SiteAddressImage,
        })
      );
      const url = (result.payload as { url: string }).url;
      updateAnswer(index, url);
    }
  };

  useEffect(() => {
    const updateFirstQuestion = () => {
      setAnswers((prevQuestions) => {
        const updatedQuestions = [...prevQuestions];
        updatedQuestions[2].question = `I, ${
          coach?.user?.firstName || ''
        }, confirm that I have observed the programme of ${name || ''} at ${
          location || ''
        } on this day of ${formattedDate || ''}.`;
        return updatedQuestions;
      });
    };

    updateFirstQuestion();
  }, [coach?.user?.firstName, name, location, formattedDate]);

  let signature = {} as SignatureCanvas | null;

  const saveSignature = (signature: string) => {
    const copy = Object.assign({});
    if (copy.signingSignature !== signature) {
      imageResize(signature, null, IMAGE_HEIGHT, 'image/png').then(
        (dataUrl) => {
          copy.signingSignature = dataUrl ?? null;

          if (copy.signingSignature != null)
            updateAnswer(3, copy.signingSignature);
        }
      );
    }
  };

  const clearSignature = () => {
    signature?.clear();
    saveSignature('');
  };

  const stringToBool = (str: string) => str.toLowerCase() === 'true';

  const updateAnswer = (indexToUpdate: number, newAnswer: string) => {
    setAnswers((prevQuestions) => {
      const updatedQuestions = prevQuestions.map((item, index) =>
        index === indexToUpdate ? { ...item, answer: newAnswer } : item
      );

      // Update section questions with the updated array
      setSectionQuestions?.([
        {
          visitSection: step18VisitSection,
          questions: updatedQuestions,
        },
      ]);

      return updatedQuestions; // Ensure the answers state is updated
    });
  };

  useEffect(() => {
    if (siteImageOne !== '' && questions[0].answer === '') {
      uploadImage(0);
    }
    if (siteImageTwo !== '' && questions[1].answer === '') {
      uploadImage(1);
    }
  }, [siteImageOne, siteImageTwo]);

  const isViewAnswers = isView || !!visitIdFromPractitionerJourney;

  useEffect(() => {
    if (
      (questions[0].answer !== '' &&
        questions[3].answer !== '' &&
        questions[2].answer.toLowerCase() !== 'false') ||
      isViewAnswers
    )
      setEnableButton?.(true);
    else setEnableButton?.(false);
  }, [setEnableButton, signature, questions]);

  return (
    <div className="p-4">
      <Typography type="h2" text="Capture site evidence" color="textDark" />
      <Divider dividerType="dashed" />

      {isViewAnswers && (
        <Alert
          className="mt-4"
          type="warning"
          title="You are viewing this form and cannot edit responses."
        />
      )}

      <div key={'Question 1'}>
        <Typography type="body" text={questions[0].question} color="primary" />
        {!isViewAnswers ? (
          <>
            <ImageInput
              className="mb-4"
              acceptedFormats={imageExtensions}
              resolutionLimit={IMAGE_WIDTH}
              label=""
              nameProp="SitePhotoOne"
              icon="CameraIcon"
              iconContainerColor="tertiary"
              currentImageString={siteImageOne}
              overrideOnClick={() => setPhotoActionBarVisibleOne(true)}
            />
            {isPhotoActionBarVisibleOne && (
              <Dialog visible position={DialogPosition.Bottom} stretch>
                <PhotoPrompt
                  title="Collage photo"
                  resolutionLimit={IMAGE_WIDTH}
                  onClose={() => setPhotoActionBarVisibleOne(false)}
                  onAction={(base64: string) => {
                    updateAnswer(0, '');
                    setSiteImageOne(base64);
                    setPhotoActionBarVisibleOne(false);
                  }}
                  onDelete={() => {
                    updateAnswer(0, '');
                    setSiteImageOne('');
                    setPhotoActionBarVisibleOne(false);
                  }}
                />
              </Dialog>
            )}
          </>
        ) : (
          <img src={sortedQuestions[0].answer as string} alt="imageOne" />
        )}
      </div>

      {/* Question Two */}
      {((siteImageOne !== '' &&
        siteImageOne != null &&
        !isView &&
        !isViewAnswers) ||
        (isViewAnswers &&
          sortedQuestions[3].answer !== '' &&
          sortedQuestions[3].answer !== undefined)) && (
        <div key={'Question 2'}>
          <Typography
            type="body"
            text={questions[1].question}
            color="primary"
          />
          {!isViewAnswers ? (
            <>
              <ImageInput
                className="mb-4"
                acceptedFormats={imageExtensions}
                resolutionLimit={IMAGE_WIDTH}
                label=""
                nameProp="SitePhotoTwo"
                icon="CameraIcon"
                iconContainerColor="tertiary"
                currentImageString={siteImageTwo}
                overrideOnClick={() => setPhotoActionBarVisibleTwo(true)}
              />
              {isPhotoActionBarVisibleTwo && (
                <Dialog visible position={DialogPosition.Bottom} stretch>
                  <PhotoPrompt
                    title="Collage photo"
                    resolutionLimit={IMAGE_WIDTH}
                    onClose={() => setPhotoActionBarVisibleTwo(false)}
                    onAction={(base64: string) => {
                      updateAnswer(1, '');
                      setSiteImageTwo(base64);
                      setPhotoActionBarVisibleTwo(false);
                    }}
                    onDelete={() => {
                      setSiteImageTwo('');
                      updateAnswer(1, '');
                      setPhotoActionBarVisibleTwo(false);
                    }}
                  />
                </Dialog>
              )}
            </>
          ) : (
            <img src={sortedQuestions[3].answer as string} alt="imageTwo" />
          )}
        </div>
      )}

      <Typography
        type="h2"
        text="Confirmation and signature"
        color="textDark"
      />
      <Divider dividerType="dashed" />
      <div className="flex flex-col gap-2 py-4">
        <CheckboxGroup
          disabled={isViewAnswers}
          checkboxColor="primary"
          titleWeight="normal"
          id="Physical visit agreement"
          key="Agreement"
          title={
            isViewAnswers ? sortedQuestions[1].question : questions[2].question
          }
          checked={
            isViewAnswers
              ? sortedQuestions &&
                stringToBool(sortedQuestions[1].answer as string)
              : stringToBool(questions[2].answer)
          }
          value={!isViewAnswers ? questions[2].answer : sortedQuestions[1]}
          onChange={() =>
            updateAnswer(2, (!stringToBool(questions[2].answer)).toString())
          }
        />
      </div>

      <Typography
        type={'h4'}
        text={`Give the phone to ${name} & ask them to sign below:`}
        color={'textDark'}
        className={'my-3'}
      />
      <Typography
        type={'body'}
        text={'Practitioner signature:'}
        color={'primary'}
      />

      {!isViewAnswers ? (
        <>
          <SignatureCanvas
            canvasProps={{
              className:
                'w-full bg-white h-60 rounded mb-4 mt-4 border border-textDark',
            }}
            ref={(ref) => {
              signature = ref;
            }}
          />
          <div className="ml-3 flex w-full items-center justify-center">
            <XCircleIcon
              className="text-errorMain h-11 w-11"
              onClick={clearSignature}
            />
            <CheckCircleIcon
              className="text-successMain h-11 w-11"
              onClick={() => {
                // Placeholder to see base64 to confirm it is working.
                const signatureString = signature!
                  .getTrimmedCanvas()
                  .toDataURL('image/png');
                saveSignature(signatureString);
              }}
            />
          </div>
        </>
      ) : (
        <>
          <img
            src={sortedQuestions && (sortedQuestions[2].answer as string)}
            alt="signature"
          />
        </>
      )}
    </div>
  );
};
